import React from "react";
import { ComponentSourceGroup } from "../../../api/models/component-source-group";
import { formatMessageWithParams } from "../../../utils/helpers/LanguageHelper";
import { CAPTION_CHANGE_COMPONENT_SOURCE_MASTER_SECTION } from "../outlineCaptions";
import { InstanceList } from "./InstanceList";
import { SourceDocumentCard } from "./SourceDocumentCard";
import { PendingSourceChange } from "./usePendingSourceChanges";

export interface ComponentSourceListProps {
  /** Source-document groups to render, already resolved and ordered by the backend; rendered as received. */
  groups: ComponentSourceGroup[];
  /** Containing folder paths keyed by source document id. */
  pathsByDocumentId?: Record<string, string>;
  /** Pending source-change entries keyed by the group's `documentId`. */
  pendingChanges?: Record<string, PendingSourceChange>;
  /** Forwarded to each card's `onSearchClick`. */
  onSearchClick?: (group: ComponentSourceGroup) => void;
  /** Forwarded to each card's `onUndoChange`. */
  onUndoChange?: (group: ComponentSourceGroup) => void;
}

/**
 * Renders the list section: a count label followed by one `SourceDocumentCard` per group, each
 * wrapping its `InstanceList`. Renders nothing when `groups` is empty.
 */
export function ComponentSourceList({
  groups,
  pathsByDocumentId,
  pendingChanges,
  onSearchClick,
  onUndoChange,
}: ComponentSourceListProps) {
  if (groups.length === 0) {
    return null;
  }

  const sectionLabel = formatMessageWithParams(CAPTION_CHANGE_COMPONENT_SOURCE_MASTER_SECTION, {
    count: groups.length,
  });

  return (
    <div data-testid="component-source-list">
      <div
        data-testid="component-source-list-label"
        style={{ color: "var(--color-purple)", fontSize: 14, fontWeight: 600, margin: "10px 0" }}
      >
        {sectionLabel}
      </div>
      {groups.map((group) => (
        <SourceDocumentCard
          group={group}
          key={group.documentId}
          onSearchClick={onSearchClick}
          onUndoChange={onUndoChange}
          path={pathsByDocumentId?.[group.documentId]}
          pendingChange={pendingChanges?.[group.documentId]}
        >
          <InstanceList instances={group.instances} />
        </SourceDocumentCard>
      ))}
    </div>
  );
}
